// Copyright (c) Bentley Systems
import { Button, ProgressRadial, Text, useToaster } from "@itwin/itwinui-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LabeledInputComponent from "../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../Helpers/enumerators";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  checkInternalServerError,
  getQueryParam,
  getUrlBasedOnEnv,
  setErrorState,
} from "../Helpers/helper-functions";
import { selectAccessToken } from "../app/authorization-slice";
import { mapResponseToDTO } from "../Helpers/map-response";
import { BpConnectionType } from "./Models/bp-connection-type";
import { SvgDelete } from "@itwin/itwinui-icons-react";
import { setConfirmation } from "../app/confirmation-slice";
import { DawComponent } from "../Helpers/daw-component";
import { useNodePermission } from "../Helpers/useNodePermission";
const LinkageAttributeForm = () => {
  // Read the URL parameters
  const iTwinId = getQueryParam("iTwinId");
  const iModelId = getQueryParam("iModelId");
  const [isUrlQueryValid, setIsUrlQueryValid] = useState<boolean>(true);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isFormValid, setisFormValid] = useState<boolean>(false);
  const [isFormDataChanged, setIsFormDataChanged] = useState<boolean>(false);
  const [savedBpConnType, setSavedBpConnType] = useState<
    BpConnectionType | undefined
  >(undefined);
  const [scope, setScope] = useState<string | undefined>(undefined);
  const [attribute, setAttribute] = useState<string | undefined>(undefined);
  const [className, setClassName] = useState<string | undefined>(undefined);
  const { isNodeAllowed: isBdpiNodeAllowed } = useNodePermission(
    DawComponent.DAW_COMPONENT_LINKAGE_ATTRIBUTE_FORM,
    {
      enabled: !!(iTwinId && iModelId),
      extraHeaders: iTwinId
        ? { "Itwin-ID": iTwinId }
        : undefined,
    },
  );

  const accessToken = useAppSelector(selectAccessToken);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const wasFormChanged = useCallback((): boolean => {
    if (savedBpConnType) {
      if (
        savedBpConnType.schemaType === scope &&
        savedBpConnType.synchFieldType === attribute &&
        savedBpConnType.className === className
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      if (scope || attribute || className) {
        return true;
      } else {
        return false;
      }
    }
  }, [scope, attribute, className, savedBpConnType]);

  useMemo(() => {
    const isFormChanged = wasFormChanged();
    setIsFormDataChanged(isFormChanged);
  }, [wasFormChanged]);

  useMemo(() => {
    if (scope && attribute) {
      setisFormValid(true);
    } else {
      setisFormValid(false);
    }
  }, [scope, attribute]);

  useEffect(() => {
    if (iTwinId && iModelId && isBdpiNodeAllowed) {
      setLoading(true);
      const abortController = new AbortController();

      const params = new URLSearchParams({
        iModelId,
        iTwinId,
      });
      const url = getUrlBasedOnEnv(
        `/api/EntryScreen/BpConnectionType?${params.toString()}`,
      );
      const propsRunId = crypto.randomUUID();
      fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: propsRunId,
        },
        signal: abortController.signal,
      })
        .then(async function (response) {
          if (!response.ok) {
            setErrorState(response.status, dispatch, response.headers.get("X-Run-Id") ?? propsRunId);
            return;
          }
          // Handle 204 No Content
          if (response.status === 204) {
            return null;
          }
          return response.json();
        })
        .then(function (data) {
          if (!data) {
            return;
          }
          const model = mapResponseToDTO<
            BpConnectionType,
            typeof data
          >(data);
          setSavedBpConnType(model);
          setScope(model.schemaType);
          setAttribute(model.synchFieldType);
          setClassName(model.className);
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            setErrorState(500, dispatch, propsRunId, true);
          }
        })
        .finally(() => setLoading(false));

      // Cleanup function
      return () => {
        abortController.abort();
      };
    } else {
      setLoading(false);
    }
  }, [iTwinId, iModelId, isBdpiNodeAllowed, accessToken, dispatch]);



  const toaster = useToaster();

  useEffect(() => {
    if (!iTwinId) {
      toaster.negative("iTwinId query parameter is missing.", {
        hasCloseButton: true,
        type: "persisting",
      });
      setIsUrlQueryValid(false);
    }
    if (!iModelId) {
      toaster.negative("iModelId query parameter is missing.", {
        hasCloseButton: true,
        type: "persisting",
      });
      setIsUrlQueryValid(false);
    }
  }, [iTwinId, iModelId, toaster]);
  const displayInformationalToast = (
    infoMsg: string,
    duration: number = 7000,
  ) => {
    if (infoMsg) {
      toaster.informational(infoMsg, {
        duration,
        hasCloseButton: true,
        type: "temporary",
      });
    }
  };

  function getSaveJson() {
    const saveBpConnectionTypeDto: BpConnectionType = {
      iModelId: iModelId as string,
      projectId: iTwinId as string,
      schemaType: scope as string,
      synchFieldType: attribute as string,
      className: className ? className : undefined,
      id: undefined,
    };
    const jsonBody = JSON.stringify(saveBpConnectionTypeDto);

    return jsonBody;
  }

  const save = async () => {
    setLoading(true);
    let saveUrl = getUrlBasedOnEnv(`/api/EntryScreen/BpConnectionType`);
    if (savedBpConnType?.id !== undefined) {
      saveUrl += `/${encodeURIComponent(savedBpConnType.id)}`;
    }
    const jsonBody = getSaveJson();
    let responseStatus = 0;
    const saveRunId = crypto.randomUUID();
    try {
      const fetchResponse = await fetch(saveUrl, {
        method: savedBpConnType ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: saveRunId,
        },
        body: jsonBody,
      }).then(async function (response) {
        responseStatus = response.status;
        // Handle 204 No Content (success) before error handling
        if (response.status === 204) {
          return { isSuccess: true };
        }
        checkInternalServerError(response.status, dispatch, saveRunId);
        return response.json();
      });
      if (fetchResponse.isSuccess === false || fetchResponse.errors) {
        setErrorState(responseStatus, dispatch, saveRunId);
      } else {
        displayInformationalToast(
          `Linkage attribute ${savedBpConnType ? "updated" : "saved"} successfully.`,
        );
        const url = `/entry${  window.location.search}`;
        void navigate(url);
      }
    } catch {
      setErrorState(500, dispatch, saveRunId);
    } finally {
      setLoading(false);
    }
  };

  const deleteAtt = async () => {
    setLoading(true);
    let saveUrl = getUrlBasedOnEnv(`/api/EntryScreen/BpConnectionType`);
    if (savedBpConnType?.id !== undefined) {
      saveUrl += `/${encodeURIComponent(savedBpConnType.id)}`;
    }
    let responseStatus = 0;
    const saveRunId = crypto.randomUUID();
    try {
      const fetchResponse = await fetch(saveUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: saveRunId,
        },
      }).then(async function (response) {
        responseStatus = response.status;
        // Handle 204 No Content (success) before error handling
        if (response.status === 204) {
          return { isSuccess: true };
        }
        checkInternalServerError(response.status, dispatch, saveRunId);
        return response.json();
      });
      if (fetchResponse.isSuccess === false) {
        setErrorState(responseStatus, dispatch, fetchResponse.runId);
      } else {
        displayInformationalToast(`Linkage attribute deleted successfully.`);
        const url = `/entry${  window.location.search}`;
        void navigate(url);
      }
    } catch {
      setErrorState(500, dispatch, saveRunId);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ height: "100%", overflowY: "hidden" }}
      className="iuiBackgroundColor"
    >
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="becs-infopanelbody-footer-line">
            <div className="becs-all-margin">
              <div className="rowWrap">
                <Text as="h3" variant="title">
                  Linkage Attribute
                </Text>
              </div>
            </div>
          </div>
          <div
            className="becs-infopanelbody-footer-line"
            style={{
              flex: 1,
              overflowY: "auto",
            }}
          >
            <div className="becs-all-margin">
              <LabeledInputComponent
                required={true}
                validationType={EfFieldValidationTypeEnum.Default}
                displayStyle="default"
                label="Associated data synch scope"
                placeholder="Enter text here..."
                disabled={!isUrlQueryValid}
                key="ScopeInput"
                defaultValue={scope}
                onChange={(currentValue) => {
                  setScope(currentValue.target.value);
                }}
                maxLength={100}
              />
              <div className="becs-t-margin">
                <LabeledInputComponent
                  required={true}
                  validationType={EfFieldValidationTypeEnum.Default}
                  displayStyle="default"
                  label="Associated data provider synch attribute"
                  placeholder="Enter text here..."
                  disabled={!isUrlQueryValid}
                  key="AttrInput"
                  defaultValue={attribute}
                  onChange={(currentValue) => {
                    setAttribute(currentValue.target.value);
                  }}
                  maxLength={100}
                />
              </div>
              <div className="becs-t-margin">
                <LabeledInputComponent
                  required={false}
                  validationType={EfFieldValidationTypeEnum.Default}
                  displayStyle="default"
                  label="Class Name"
                  placeholder="Enter text here..."
                  disabled={!isUrlQueryValid}
                  key="ClassNameInput"
                  defaultValue={className}
                  onChange={(currentValue) => {
                    setClassName(currentValue.target.value);
                  }}
                  maxLength={100}
                />
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div className="becs-all-margin">
              <div className="rowWrap">
                <Button
                  key="saveCredentialBtn"
                  as="button"
                  onClick={() => {
                    void save();
                  }}
                  styleType="cta"
                  disabled={!(isFormValid && isFormDataChanged)}
                >
                  <div>{savedBpConnType ? "Update" : "Save"}</div>
                </Button>
                <Button
                  key="deleteBtn"
                  startIcon={<SvgDelete />}
                  as="button"
                  onClick={() => {
                    dispatch(
                      setConfirmation({
                        isConfirmationOpen: true,
                        onOkAction: () => {
                          void deleteAtt();
                        },
                        caption: "Delete confirmation",
                        text: "Are you sure you want to delete Linkage Attribute?",
                        okText: "Yes",
                        cancelText: "No",
                      }),
                    );
                  }}
                  disabled={!(isUrlQueryValid && savedBpConnType !== undefined)}
                >
                  <div>Delete</div>
                </Button>
                <Button
                  key="cancelBtn"
                  as="button"
                  onClick={() => {
                    const url = `/entry${  window.location.search}`;
                    void navigate(url);
                  }}
                >
                  <div>Cancel</div>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkageAttributeForm;
